import { z } from 'zod'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import type { ScorecardMetricDataType } from '@/services/scorecard.service'
import type {
  CreateOgsmNodeInput,
  OgsmBoardNode,
  OgsmDirection,
  OgsmTargetType,
  UpdateOgsmNodeInput,
} from '@/types/api'

export const NEW_OBJECTIVE_DIRECTIONS = [
  'INCREASE',
  'DECREASE',
  'MAINTAIN',
] as const satisfies readonly OgsmDirection[]

export type ObjectiveDialogTarget =
  | { mode: 'create' }
  | { mode: 'create-child'; parent: OgsmBoardNode }
  | { mode: 'edit'; node: OgsmBoardNode }

/** METRIC is a company metric link; the rest are CUSTOM nodes of that target type. */
export const GOAL_TYPES = [
  'METRIC',
  'NUMBER',
  'CURRENCY',
  'PERCENT',
  'DATE',
  'TEXT',
] as const

export type GoalType = (typeof GOAL_TYPES)[number]

export const NUMERIC_GOAL_TYPES = [
  'METRIC',
  'NUMBER',
  'CURRENCY',
  'PERCENT',
] as const satisfies readonly GoalType[]

export const NON_NUMERIC_GOAL_TYPES = [
  'DATE',
  'TEXT',
] as const satisfies readonly GoalType[]

export const ROOT_GOAL_TYPES: readonly GoalType[] = GOAL_TYPES

export const CHILD_GOAL_TYPES: readonly GoalType[] = GOAL_TYPES

export const TEXT_GOAL_MAX = 500

export type ObjectiveFormMode = 'root' | 'child'

/** `metricLinking` false (no metric source, the portal) drops Chronograph metric. */
export function goalTypesFor(
  mode: ObjectiveFormMode,
  metricLinking = true
): readonly GoalType[] {
  const types = mode === 'root' ? ROOT_GOAL_TYPES : CHILD_GOAL_TYPES
  return metricLinking ? types : types.filter((type) => type !== 'METRIC')
}

/** Only a root metric can feed the Scorecard, so only the root starts on Chronograph metric. */
export function defaultGoalType(
  mode: ObjectiveFormMode,
  metricLinking = true
): GoalType {
  return mode === 'root' && metricLinking ? 'METRIC' : 'NUMBER'
}

export function isGoalType(value: string): value is GoalType {
  return GOAL_TYPES.some((type) => type === value)
}

export function isNumericGoalType(type: GoalType): boolean {
  return NUMERIC_GOAL_TYPES.some((numeric) => numeric === type)
}

/** What the create dialog's form holds; each goal type reads only its own fields. */
export interface ObjectiveFormValues {
  goalType: GoalType
  direction: OgsmDirection
  /** '' until a metric is chosen (METRIC). */
  metricLabelId: string
  /** NUMBER, CURRENCY and PERCENT. */
  title: string
  /** DATE and TEXT, optional. */
  verb: string
  /** DATE and TEXT. */
  descriptor: string
  /** Numeric types; null while the goal input is empty. */
  targetValue: number | null
  /** TEXT. */
  targetText: string
  /** YYYY-MM-DD */
  targetDate: string
  includeInScorecard: boolean
}

interface FormIssue {
  path: keyof ObjectiveFormValues
  message: string
}

function goalIssues(
  values: ObjectiveFormValues,
  mode: ObjectiveFormMode
): FormIssue[] {
  const { errors } = OGSM_COPY
  const issues: FormIssue[] = []
  const { goalType } = values
  if (goalType === 'METRIC' && values.metricLabelId === '') {
    issues.push({ path: 'metricLabelId', message: errors.metricRequired })
  }
  if (
    (goalType === 'NUMBER' ||
      goalType === 'CURRENCY' ||
      goalType === 'PERCENT') &&
    values.title.trim() === ''
  ) {
    issues.push({ path: 'title', message: errors.titleRequired })
  }
  if (
    isNumericGoalType(goalType) &&
    (values.targetValue === null || !Number.isFinite(values.targetValue))
  ) {
    issues.push({ path: 'targetValue', message: errors.goalRequired })
  }
  if (
    (goalType === 'DATE' || goalType === 'TEXT') &&
    values.descriptor.trim() === ''
  ) {
    issues.push({
      path: 'descriptor',
      message:
        mode === 'child' ? errors.strategyRequired : errors.objectiveRequired,
    })
  }
  if (goalType === 'TEXT') {
    if (values.targetText.trim() === '') {
      issues.push({ path: 'targetText', message: errors.goalRequired })
    } else if (values.targetText.length > TEXT_GOAL_MAX) {
      issues.push({ path: 'targetText', message: errors.textTooLong })
    }
  }
  if (values.targetDate === '') {
    issues.push({ path: 'targetDate', message: errors.dateRequired })
  }
  return issues
}

/** Required fields only; the backend's 400 covers every business rule. */
const buildObjectiveFormSchema = (mode: ObjectiveFormMode) =>
  z
    .object({
      goalType: z.enum(GOAL_TYPES),
      direction: z.enum(NEW_OBJECTIVE_DIRECTIONS),
      metricLabelId: z.string(),
      title: z.string(),
      verb: z.string(),
      descriptor: z.string(),
      targetValue: z.number().nullable(),
      targetText: z.string(),
      targetDate: z.string(),
      includeInScorecard: z.boolean(),
    })
    .superRefine((values, ctx) => {
      for (const issue of goalIssues(values, mode)) {
        ctx.addIssue({
          code: 'custom',
          path: [issue.path],
          message: issue.message,
        })
      }
    })

const OBJECTIVE_FORM_SCHEMAS = {
  root: buildObjectiveFormSchema('root'),
  child: buildObjectiveFormSchema('child'),
}

/** The create form's schema; a child's messages name a strategy. */
export function objectiveFormSchemaFor(mode: ObjectiveFormMode) {
  return OBJECTIVE_FORM_SCHEMAS[mode]
}

export function defaultObjectiveFormValues(
  periodEnd: string,
  mode: ObjectiveFormMode,
  metricLinking = true
): ObjectiveFormValues {
  return {
    goalType: defaultGoalType(mode, metricLinking),
    direction: 'INCREASE',
    metricLabelId: '',
    title: '',
    verb: '',
    descriptor: '',
    targetValue: null,
    targetText: '',
    targetDate: periodEnd,
    includeInScorecard: mode === 'root',
  }
}

/** Formatting only: the backend stores the title exactly as sent. */
export function composeObjectiveTitle(
  values: ObjectiveFormValues,
  metricName: string
): string {
  switch (values.goalType) {
    case 'METRIC':
      return `${OGSM_COPY.directions[values.direction]} ${metricName}`.trim()
    case 'NUMBER':
    case 'CURRENCY':
    case 'PERCENT':
      return values.title.trim()
    case 'DATE':
    case 'TEXT':
      return `${values.verb.trim()} ${values.descriptor.trim()}`.trim()
  }
}

export function objectivePreview(
  values: ObjectiveFormValues,
  metricName: string | undefined
): string {
  if (values.goalType === 'METRIC') {
    return metricName
      ? composeObjectiveTitle(values, metricName)
      : `${OGSM_COPY.directions[values.direction]} …`
  }
  if (values.goalType !== 'DATE' && values.goalType !== 'TEXT') {
    return composeObjectiveTitle(values, '')
  }
  if (values.descriptor.trim()) return composeObjectiveTitle(values, '')
  const verb = values.verb.trim()
  return verb ? `${verb} …` : '…'
}

export function metricUnitLabel(
  dataType: ScorecardMetricDataType | null | undefined
): string {
  return OGSM_COPY.unitKinds[dataType ?? 'NUMBER']
}

/**
 * One mapping for the root and every child. `parentId` is sent only under a
 * parent, and the Scorecard flag only at the root: the backend refuses it below depth 0.
 */
export function toCreateNodeInput(
  values: ObjectiveFormValues,
  context: { parentId?: string; metricName: string }
): CreateOgsmNodeInput {
  const { parentId, metricName } = context
  const under = parentId ? { parentId } : {}
  const title = composeObjectiveTitle(values, metricName)
  const value =
    values.targetValue !== null ? { targetValue: values.targetValue } : {}
  const { goalType, targetDate } = values
  switch (goalType) {
    case 'METRIC':
      // No targetType: the backend takes it from the metric.
      return {
        ...under,
        kind: 'METRIC',
        title,
        direction: values.direction,
        metricLabelId: values.metricLabelId,
        ...value,
        targetDate,
        ...(parentId ? {} : { includeInScorecard: values.includeInScorecard }),
      }
    case 'NUMBER':
    case 'CURRENCY':
    case 'PERCENT':
      return {
        ...under,
        kind: 'CUSTOM',
        title,
        direction: values.direction,
        targetType: goalType,
        ...value,
        targetDate,
      }
    case 'DATE':
      return { ...under, kind: 'CUSTOM', title, targetType: 'DATE', targetDate }
    case 'TEXT':
      return {
        ...under,
        kind: 'CUSTOM',
        title,
        targetType: 'TEXT',
        targetText: values.targetText.trim(),
        targetDate,
      }
  }
}

export function isNumericTarget(type: OgsmTargetType): boolean {
  return type === 'NUMBER' || type === 'CURRENCY' || type === 'PERCENT'
}

/** Label mapping only: a METRIC node reads as Chronograph metric, a CUSTOM one as its target type. */
export function goalTypeOf(
  node: Pick<OgsmBoardNode, 'kind' | 'targetType'>
): GoalType {
  return node.kind === 'METRIC' ? 'METRIC' : node.targetType
}

/** Only a Chronograph metric goal edits its metric; a custom goal keeps its type and has none. */
export function editsMetric(
  node: Pick<OgsmBoardNode, 'kind' | 'targetType' | 'canLinkMetric'>,
  metricLinking = true
): boolean {
  return (
    metricLinking &&
    node.kind === 'METRIC' &&
    node.canLinkMetric &&
    isNumericTarget(node.targetType)
  )
}

export interface EditObjectiveValues {
  title: string
  direction: OgsmDirection | null
  metricLabelId: string | null
}

export const editObjectiveSchema = z.object({
  title: z
    .string()
    .refine((title) => title.trim() !== '', OGSM_COPY.errors.titleRequired),
  direction: z.enum(NEW_OBJECTIVE_DIRECTIONS).nullable(),
  metricLabelId: z.string().nullable(),
})

export function defaultEditObjectiveValues(
  node: OgsmBoardNode
): EditObjectiveValues {
  return {
    title: node.title,
    direction: node.direction ?? null,
    metricLabelId: node.metricLabelId ?? null,
  }
}

/** Only what changed. The goal itself changes through a revision, never here. */
export function toUpdateObjectiveInput(
  node: OgsmBoardNode,
  values: EditObjectiveValues,
  metricLinking = true
): UpdateOgsmNodeInput {
  const title = values.title.trim()
  const input: UpdateOgsmNodeInput = {}
  if (title !== node.title) input.title = title
  if (
    values.direction !== null &&
    values.direction !== (node.direction ?? null)
  ) {
    input.direction = values.direction
  }
  if (
    editsMetric(node, metricLinking) &&
    values.metricLabelId !== null &&
    values.metricLabelId !== (node.metricLabelId ?? null)
  ) {
    input.metricLabelId = values.metricLabelId
  }
  return input
}
